import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Typography,
  MenuItem,
  Button,
  Menu,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  Tooltip,
  IconButton,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ccToPcConverter } from 'helpers/commonUtils';
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { useDebounce } from 'helpers';
import DateFilterPopover from 'views/Reports/components/DateFilterPopover/DateFilterPopover';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  button: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selected: {
    color: theme.palette.primary.main,
    margin: '0px 8px'
  },
  regexFilterPanel: {
    padding: '8px',
    width: '450px'
  },
  noOptions: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px'
  },
  filterActionPanel: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  filterDisplayItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px',
    gap: '16px'
  },
  selectedLabel: {
    width: '45%'
  },
  selectedField: {
    width: '40%',
    minWidth: 220,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  selectedDeleteIcon: {
    width: '10%',
    color: theme.palette.error.main
  },
  actionIcon: {
    margin: '0px 8px',
    fontWeight: 'normal',
    opacity: '1 !important',
    color: theme.palette.primary.main + '!important',
    '&.Mui-disabled': {
      color: 'rgba(0,0,0,.55) !important'
    }
  },
  filterDisplayPanel: {
    overflow: 'auto',
    maxHeight: '35vh'
  },
  appliedFilters: {
    zIndex: '2000 !important'
  },
  availableFilters: {
    zIndex: '3000 !important'
  }
}));

export default function RegexFilter(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { filter, action, validation } = props;
  const {
    options,
    onChange,
    title,
    interDependency,
    intraDependency,
    backdropBlur,
    enableMonthYearPicker
  } = filter;

  const [isSelected, setIsSelected] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [searchValue, setSearchValue] = useState({});
  const [searchApiValue, setSearchApiValue] = useState({});
  const search = useDebounce(searchApiValue, 500);
  const anchorRefE1 = React.useRef(null);
  const anchorRefE2 = React.useRef(null);
  const prevInterDependencyRef = React.useRef(interDependency);
  const [dateAnchorEls, setDateAnchorEls] = useState({});
  const [dateFilterDrafts, setDateFilterDrafts] = useState({});
  const [rangeSelectionSteps, setRangeSelectionSteps] = useState({});
  const [visibleDailyMonths, setVisibleDailyMonths] = useState({});

  useEffect(() => {
    if (options && options.length > 0) {
      setIsSelected(options.filter((option) => option.checked).length > 0);
    }
  }, [options]);

  useEffect(() => {
    setIsSelected(
      Object.keys(searchValue).length > 0 &&
        options.filter((option) => option.checked).length > 0
    );
  }, [intraDependency]);

  useEffect(() => {
    const prevInterDependency = prevInterDependencyRef.current;
    const hasChanged =
      !prevInterDependency ||
      prevInterDependency.length !== interDependency?.length ||
      !prevInterDependency.every(
        (item, index) => item === interDependency?.[index]
      );

    if (hasChanged) {
      setSearchValue({});
      setSearchApiValue({});
      prevInterDependencyRef.current = interDependency;
    }
  }, [interDependency]);

  useEffect(() => {
    let newOptions = options.map((option) => {
      if (searchValue.hasOwnProperty(option.value)) {
        option.regex = searchValue[option.value];
      }
      return option;
    });
    onChange(newOptions.filter((option) => option.checked));
  }, [searchValue]);

  useEffect(() => {
    if (open) {
      dispatch(action);
    }
  }, [search]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    if (validation) {
      validation?.validate(searchValue);
    }
  };

  const anyDatePopoverOpenRef = React.useRef(false);

  const handleClose = () => {
    if (anyDatePopoverOpenRef.current) return;
    setOpen(false);
  };

  const resetFilters = () => {
    onChange(
      options.map((option) => {
        return { ...option, checked: false, regex: '' };
      })
    );
    setSearchValue({});
    dispatch(action);
  };

  const applyFilters = () => {
    dispatch(action);
  };

  const handleSelectionChange = (event) => {
    let newOptions = options.map((option) => {
      if (option.value === event.value) {
        option.checked = !option.checked;
        option.regex = '';
        if (!option.checked) {
          let temp = { ...searchValue };
          delete temp[option.value];
          setSearchValue(temp);
        } else {
          setSearchValue({ ...searchValue, [option.value]: '' });
        }
      } else {
        if (option.checked && searchValue.hasOwnProperty(option.value)) {
          option.regex = searchValue[option.value];
        }
      }
      return option;
    });
    if (newOptions.filter((option) => !option.checked).length === 0) {
      setSelectOpen(false);
    }
    onChange(newOptions.filter((option) => option.checked));
  };

  const handleDeleteSelection = (event) => {
    handleSelectionChange(event);
    dispatch(action);
  };

  const handleRegexChange = (event, option) => {
    let value;
    if (option.dataType === 'Boolean') {
      value = event.target.value;
    } else if (option.dataType === 'String' || !option.dataType) {
      value = event.target.value;
    }
    if (validation) {
      validation?.validate({
        ...searchValue,
        [event.target?.id || option.value]: value
      });
    }
    setSearchValue({
      ...searchValue,
      [event.target?.id || option.value]: value
    });
    setSearchApiValue({
      ...searchValue,
      [event.target?.id || option.value]: value
    });
  };

  return (
    <>
      <Tooltip title={options.length === 0 ? `Not applicable` : ``}>
        <div>
          <Button
            aria-label={`Filter ${title}`}
            ref={anchorRefE1}
            className={isSelected ? classes.selected : classes.button}
            onClick={handleToggle}
            disabled={options.length === 0}
            endIcon={<ExpandMoreIcon />}
          >
            {title}
          </Button>
        </div>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRefE1.current}
        className={classes.appliedFilters}
        role={'presentation'}
        transition
        placement="bottom-start"
        style={{ zIndex: 1400 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper
              style={
                Object.values(dateAnchorEls).some((el) => el !== null)
                  ? { filter: 'blur(4px)', transition: 'filter 0.2s' }
                  : {}
              }
            >
              <ClickAwayListener onClickAway={() => handleClose()}>
                <div className={classes.regexFilterPanel}>
                  <div className={classes.filterDisplayPanel}>
                    <div className={classes.filterDisplay}>
                      {options.filter((option) => option.checked).length ===
                      0 ? (
                        <Typography
                          variant="inherit"
                          className={classes.noOptions}
                        >
                          No filters selected
                        </Typography>
                      ) : (
                        <form autoComplete="off">
                          {options
                            .filter((option) => option.checked)
                            .map((option, index) => {
                              const currentDateValue =
                                searchValue[option.value] || option.regex;
                              return (
                                <div
                                  key={index}
                                  className={classes.filterDisplayItem}
                                >
                                  <Typography
                                    variant="inherit"
                                    className={classes.selectedLabel}
                                  >
                                    {option.label ||
                                      ccToPcConverter(option.value)}
                                  </Typography>
                                  {(option.dataType === 'String' ||
                                    !option.dataType) && (
                                    <TextField
                                      id={option.value}
                                      className={classes.selectedField}
                                      variant="standard"
                                      value={
                                        searchValue[option.value] ||
                                        option.regex ||
                                        ''
                                      }
                                      onChange={(event) =>
                                        handleRegexChange(event, option)
                                      }
                                      {...(validation && {
                                        error: validation?.shouldShowError(
                                          option.value
                                        ),
                                        helperText: validation?.getErrorMessage(
                                          option.value
                                        )
                                      })}
                                    />
                                  )}
                                  {option.dataType === 'Boolean' && (
                                    <RadioGroup
                                      id={option.value}
                                      className={classes.selectedField}
                                      value={String(
                                        searchValue[option.value] ||
                                          option.regex
                                      )}
                                      onChange={(event) =>
                                        handleRegexChange(event, option)
                                      }
                                      row
                                    >
                                      <FormControlLabel
                                        value="true"
                                        control={<Radio />}
                                        label="True"
                                      />
                                      <FormControlLabel
                                        value="false"
                                        control={<Radio />}
                                        label="False"
                                      />
                                    </RadioGroup>
                                  )}
                                  {(option.dataType === 'Date' ||
                                    option.dataType === 'DateRange') && (
                                    <>
                                      <Button
                                        className={classes.selectedField}
                                        variant="outlined"
                                        size="small"
                                        style={{
                                          minWidth: '220px',
                                          whiteSpace: 'nowrap'
                                        }}
                                        onClick={(e) => {
                                          const anchorEl = e.currentTarget;
                                          anyDatePopoverOpenRef.current = true;
                                          setDateFilterDrafts((prev) => ({
                                            ...prev,
                                            [option.value]: {
                                              rangePreset: '',
                                              fromDate:
                                                currentDateValue?.start || '',
                                              toDate:
                                                currentDateValue?.end || ''
                                            }
                                          }));
                                          setRangeSelectionSteps((prev) => ({
                                            ...prev,
                                            [option.value]: 'start'
                                          }));
                                          setVisibleDailyMonths((prev) => ({
                                            ...prev,
                                            [option.value]:
                                              currentDateValue?.end
                                                ? moment(
                                                    currentDateValue.end
                                                  ).startOf('month')
                                                : moment().startOf('month')
                                          }));
                                          setDateAnchorEls((prev) => ({
                                            ...prev,
                                            [option.value]: anchorEl
                                          }));
                                        }}
                                      >
                                        {currentDateValue?.start ||
                                        currentDateValue?.end
                                          ? `${
                                              currentDateValue?.start || '?'
                                            } → ${currentDateValue?.end || '?'}`
                                          : 'Select dates'}
                                      </Button>
                                      <DateFilterPopover
                                        hideDescription
                                        showBackdrop={!!backdropBlur}
                                        enableMonthYearPicker={
                                          !!enableMonthYearPicker
                                        }
                                        popperStyle={{ zIndex: 4000 }}
                                        anchorEl={
                                          dateAnchorEls[option.value] || null
                                        }
                                        dateFilterDraft={
                                          dateFilterDrafts[option.value] || {
                                            rangePreset: '',
                                            fromDate: '',
                                            toDate: ''
                                          }
                                        }
                                        rangeSelectionStep={
                                          rangeSelectionSteps[option.value] ||
                                          'start'
                                        }
                                        visibleDailyMonth={
                                          visibleDailyMonths[option.value] ||
                                          moment().startOf('month')
                                        }
                                        minAvailableDailyDate={moment(
                                          '1900-01-01'
                                        )}
                                        maxAvailableDailyDate={
                                          option.allowFutureDates
                                            ? moment('2100-12-31')
                                            : moment()
                                        }
                                        dateType="DAY"
                                        onDateFilterChange={(draft) =>
                                          setDateFilterDrafts((prev) => ({
                                            ...prev,
                                            [option.value]: draft
                                          }))
                                        }
                                        onRangeSelectionStepChange={(step) =>
                                          setRangeSelectionSteps((prev) => ({
                                            ...prev,
                                            [option.value]: step
                                          }))
                                        }
                                        onVisibleDailyMonthChange={(
                                          monthOrUpdater
                                        ) =>
                                          setVisibleDailyMonths((prev) => {
                                            const current =
                                              prev[option.value] ||
                                              moment().startOf('month');
                                            return {
                                              ...prev,
                                              [option.value]:
                                                typeof monthOrUpdater ===
                                                'function'
                                                  ? monthOrUpdater(current)
                                                  : monthOrUpdater
                                            };
                                          })
                                        }
                                        onApplyDateFilters={() => {
                                          const draft =
                                            dateFilterDrafts[option.value] ||
                                            {};
                                          const updatedRange = {
                                            start: draft.fromDate || '',
                                            end: draft.toDate || ''
                                          };
                                          console.log(
                                            'onApplyDateFilters',
                                            option.value,
                                            updatedRange
                                          );
                                          setSearchValue((prev) => ({
                                            ...prev,
                                            [option.value]: updatedRange
                                          }));
                                          setSearchApiValue((prev) => ({
                                            ...prev,
                                            [option.value]: updatedRange
                                          }));
                                          setDateAnchorEls((prev) => ({
                                            ...prev,
                                            [option.value]: null
                                          }));
                                          anyDatePopoverOpenRef.current = false;
                                        }}
                                        onCloseDateFilter={() => {
                                          setDateAnchorEls((prev) => ({
                                            ...prev,
                                            [option.value]: null
                                          }));
                                          anyDatePopoverOpenRef.current = false;
                                        }}
                                      />
                                    </>
                                  )}
                                  <Tooltip
                                    title={`Remove ${ccToPcConverter(
                                      option.value
                                    )} filter`}
                                  >
                                    <IconButton
                                      aria-label="delete"
                                      className={classes.selectedDeleteIcon}
                                      onClick={() =>
                                        handleDeleteSelection(option)
                                      }
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              );
                            })}
                        </form>
                      )}
                    </div>
                  </div>
                  <div className={classes.filterActionPanel}>
                    <Tooltip title="Remove all filters">
                      <Button
                        className={classes.actionIcon}
                        disabled={
                          options.filter((option) => option.checked).length ===
                          0
                        }
                        onClick={() => resetFilters()}
                      >
                        Reset
                      </Button>
                    </Tooltip>
                    <div>
                      <Tooltip title="Add filters">
                        <Button
                          className={classes.actionIcon}
                          disabled={
                            options.filter((option) => !option.checked)
                              .length === 0
                          }
                          onClick={() => setSelectOpen(true)}
                          endIcon={<AddBoxOutlinedIcon />}
                          ref={anchorRefE2}
                        >
                          Add Filters
                        </Button>
                      </Tooltip>
                      {selectOpen && (
                        <Menu
                          keepMounted
                          open={selectOpen}
                          onClose={() => setSelectOpen(false)}
                          className={classes.availableFilters}
                          anchorEl={anchorRefE2.current}
                        >
                          {options
                            .filter((option) => !option.checked)
                            .map((option, index) => (
                              <MenuItem
                                value={index}
                                onClick={() => handleSelectionChange(option)}
                              >
                                {option.label || ccToPcConverter(option.value)}
                              </MenuItem>
                            ))}
                        </Menu>
                      )}
                    </div>
                  </div>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
